/**
 * useFirstVisit Hook
 *
 * Manages first-visit detection for onboarding experiences.
 * Uses localStorage to persist whether a user has seen a particular help overlay.
 *
 * @module components/HelpOverlay/useFirstVisit
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const DEFAULT_STORAGE_KEY = 'dow:onboarding:battlefield-help-v1';
const DEFAULT_SHOW_DELAY = 1500;

export interface UseFirstVisitOptions {
  /** localStorage key to track visit status. Default: 'dow:onboarding:battlefield-help-v1' */
  storageKey?: string;
  /** Delay in ms before showing help on first visit. Default: 1500ms */
  showDelay?: number;
}

export interface UseFirstVisitReturn {
  /** True if this is the user's first visit (no localStorage entry) */
  isFirstVisit: boolean;
  /** True when help should be displayed (after delay, on first visit only) */
  shouldShowHelp: boolean;
  /** Call to mark the help as seen and persist to localStorage */
  markAsSeen: () => void;
  /** Reset the storage (for testing purposes) */
  reset: () => void;
}

/**
 * Safely reads from localStorage, handling private browsing and errors
 */
function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // localStorage may throw in private browsing or when disabled
    return null;
  }
}

/**
 * Safely writes to localStorage, handling private browsing and errors
 */
function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // localStorage may throw in private browsing or when disabled
    return false;
  }
}

/**
 * Safely removes from localStorage, handling private browsing and errors
 */
function safeRemoveItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    // localStorage may throw in private browsing or when disabled
    return false;
  }
}

/**
 * Hook to detect first-time visitors and show onboarding help after a delay.
 *
 * @param options - Configuration options
 * @returns Object with visit state and control functions
 *
 * @example
 * ```tsx
 * const { shouldShowHelp, markAsSeen } = useFirstVisit();
 *
 * return (
 *   <>
 *     <BattlefieldCanvas />
 *     {shouldShowHelp && <HelpOverlay onDismiss={markAsSeen} />}
 *   </>
 * );
 * ```
 */
export function useFirstVisit(options?: UseFirstVisitOptions): UseFirstVisitReturn {
  const storageKey = options?.storageKey ?? DEFAULT_STORAGE_KEY;
  const showDelay = options?.showDelay ?? DEFAULT_SHOW_DELAY;

  // Check localStorage on initial render only
  const [isFirstVisit, setIsFirstVisit] = useState<boolean>(() => {
    const stored = safeGetItem(storageKey);
    return stored === null;
  });

  const [shouldShowHelp, setShouldShowHelp] = useState(false);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Set up delayed show on first visit
  useEffect(() => {
    isMountedRef.current = true;

    if (!isFirstVisit) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        setShouldShowHelp(true);
      }
    }, showDelay);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
    };
  }, [isFirstVisit, showDelay]);

  const markAsSeen = useCallback(() => {
    safeSetItem(storageKey, 'true');
    setIsFirstVisit(false);
    setShouldShowHelp(false);
  }, [storageKey]);

  const reset = useCallback(() => {
    safeRemoveItem(storageKey);
    setIsFirstVisit(true);
    setShouldShowHelp(false);
  }, [storageKey]);

  return {
    isFirstVisit,
    shouldShowHelp,
    markAsSeen,
    reset,
  };
}

export default useFirstVisit;
